import React, { useState } from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";

import { LogoProvider } from "@/components/LogoProvider";
import { PaymentGuarantee } from "@/containers/PaymentGuarantee";
import { Slider } from "@/components/Slider";

import { Heading } from "@/components/Heading";
import { useTheme } from "@/hooks/useTheme";
import { Icon } from "@/components/Icon";
import { PRODUCTS } from "@/constants/products";
import { useAuth } from "@/hooks/useAuth";
import {
  PRICING_BACKUP_BASIC_PLAN,
  PRICING_BACKUP_PRO_PLAN,
  PRICING_BACKUP_ENTERPRISE_PLAN,
  PRICING_BACKUP_SLIDER_VALUES,
} from "@/constants/pricing";
import {
  FIGMA_REDIRECT_URI_BACKUP,
  getConnectFigmaAccountUrl,
} from "@/services/figmaService";
import { CountTeamFilesModal } from "@/containers/PricingSlider/CountTeamFilesModal";
import { IPricingSliderSection } from "./types";
import { BasicBackupCard, ProBackupCard, EnterpriseBackupCard } from "./Cards";
import { BuyMoreCard } from "./BuyMoreCard";
import { usePricingContext } from "../Pricing/state/context";
import { PaymentSuccessfulPopup } from "../Converter/PreviewAndPayment/components/SuccessModal";
import { PricesCurrencyText } from "../Pricing/PricesCurrencyText";
import { IFigmaBackupType } from "../Converter/types";

type IProps = IPricingSliderSection & JSX.IntrinsicElements["div"];

export const PricingSlider: React.FC<IProps> = ({
  isFigmaBackup,
  showGuarantee = false,
  figmaAccessToken,
}) => {
  const { push: redirect } = useRouter();
  const { css } = useFela();
  const { colors, bp } = useTheme();
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { send, current } = usePricingContext();
  const [isBackupCountModalOpen, setIsBackupCountModalOpen] = useState(false);
  const ctx = current.context;

  const sliderValues = {
    startValue: "1",
    values: PRICING_BACKUP_SLIDER_VALUES,
  };

  const handleTeamFilesClick = () => {
    figmaAccessToken
      ? setIsBackupCountModalOpen(true)
      : redirect(getConnectFigmaAccountUrl(FIGMA_REDIRECT_URI_BACKUP));
  };

  const [sliderValue, setSliderValue] = useState<number>(
    PRICING_BACKUP_SLIDER_VALUES[0],
  );

  const isSliderMaxValue =
    sliderValue ===
    PRICING_BACKUP_SLIDER_VALUES[PRICING_BACKUP_SLIDER_VALUES.length - 1];
  const defaultPlanFilesAmount = PRICING_BACKUP_SLIDER_VALUES[0];

  return (
    <>
      {isBackupCountModalOpen && (
        <CountTeamFilesModal
          onClose={() => setIsBackupCountModalOpen(false)}
          accessToken={figmaAccessToken}
        />
      )}
      <div
        className={css({
          position: "absolute",
          bottom: 0,
          left: 0,
          display: "none",
          [bp.FROM_LARGE_DESKTOP]: {
            display: "block",
          },
        })}
      >
        <Icon iconType="OVAL" />
      </div>
      <div
        className={css({
          position: "relative",
          zIndex: 1,
        })}
      >
        {isFigmaBackup && (
          <Heading as="h2" customStyle={{ textAlign: "center" }}>
            {t("section/pricing:title")}
          </Heading>
        )}
        <PricesCurrencyText
          customStyle={{
            padding: "10px 30px 15px",
            textAlign: "right",
            marginTop: 30,
          }}
        />
        <div
          className={css({
            minHeight: 911,
            background: colors.WHITE,
            boxShadow: "20px 20px 80px rgba(16, 5, 48, 0.08)",
            borderRadius: 12,
            padding: "27px 60px 48px",
          })}
        >
          <div
            className={css({
              [bp.FROM_DESKTOP]: {
                display: "flex",
              },
            })}
          >
            <div className={css({ flexBasis: "37%", marginRight: 56 })}>
              <Heading as="h3" customStyle={{ margin: "26px 0 16px" }}>
                {t("section/pricingFigmaBackup:slider.title")}
              </Heading>
              <p className={css({ lineHeight: "24px", color: colors.GRAY })}>
                {t("section/pricingFigmaBackup:slider.subtitle")}
              </p>
              <p
                className={css({
                  cursor: "pointer",
                  color: colors.ORANGE,
                  textDecoration: "underline",
                  marginTop: 16,
                })}
                onClick={handleTeamFilesClick}
              >
                Calculate files in your Figma team
              </p>
            </div>
            <Slider
              suffix={{
                singular: "file",
                plural: "files",
              }}
              sliderValues={sliderValues}
              onSliderChange={setSliderValue}
              customStyle={{
                flexBasis: "63%",
                marginTop: 10,
                [bp.FROM_DESKTOP]: { marginTop: 0 },
              }}
            />
          </div>

          {isSliderMaxValue ? (
            <BuyMoreCard />
          ) : (
            <div
              className={css({
                marginTop: 40,
                display: "flex",
                justifyContent: "space-around",
                flexDirection: "column",
                [bp.FROM_TABLET]: {
                  marginTop: 40,
                },
                [bp.FROM_DESKTOP]: { flexDirection: "row", marginTop: 40 },
                nested: {
                  "> div:not(:first-of-type)": {
                    marginTop: 30,
                    [bp.FROM_TABLET]: {
                      marginTop: 40,
                    },
                    [bp.FROM_DESKTOP]: {
                      marginTop: 0,
                      marginLeft: 30,
                    },
                  },
                },
              })}
            >
              <BasicBackupCard
                price={
                  PRICING_BACKUP_BASIC_PLAN[
                    sliderValue || defaultPlanFilesAmount
                  ]
                }
                cta={{
                  title: t("actions.doNow", {
                    do: t("actions.buy"),
                  }),
                  onClick: () => {
                    send({
                      type: "SET_BACKUP_PAYMENT_DATA",
                      figmaPlanId: PRODUCTS.BACKUP_BASIC as IFigmaBackupType,
                      planQuantity: sliderValue || defaultPlanFilesAmount,
                      isFigmaBackup: true,
                    });
                  },
                }}
                amount={sliderValue}
              />
              <ProBackupCard
                price={
                  PRICING_BACKUP_PRO_PLAN[sliderValue || defaultPlanFilesAmount]
                }
                cta={{
                  title: t("actions.doNow", {
                    do: t("actions.buy"),
                  }),
                  onClick: () =>
                    send({
                      type: "SET_BACKUP_PAYMENT_DATA",
                      figmaPlanId: PRODUCTS.BACKUP_PRO as IFigmaBackupType,
                      planQuantity: sliderValue || defaultPlanFilesAmount,
                      isFigmaBackup: true,
                    }),
                }}
                amount={sliderValue}
              />
              <EnterpriseBackupCard
                price={
                  PRICING_BACKUP_ENTERPRISE_PLAN[
                    sliderValue || defaultPlanFilesAmount
                  ]
                }
                cta={{
                  title: t("actions.doNow", {
                    do: t("actions.buy"),
                  }),
                  onClick: () =>
                    send({
                      type: "SET_BACKUP_PAYMENT_DATA",
                      figmaPlanId: PRODUCTS.BACKUP_ENTERPRISE as IFigmaBackupType,
                      planQuantity: sliderValue || defaultPlanFilesAmount,
                      isFigmaBackup: true,
                    }),
                }}
                amount={sliderValue}
              />
            </div>
          )}
        </div>

        <div
          className={css({
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            [bp.FROM_DESKTOP]: {
              marginTop: 60,
              justifyContent: "space-around",
              flexWrap: "no-wrap",
            },
          })}
        >
          <LogoProvider title="PayPal" src="/svgs/paypal.svg" />
          <LogoProvider title="MasterCard" src="/svgs/mastercard.svg" />
          <LogoProvider title="VISA" src="/svgs/visa.svg" />
          <LogoProvider title="Amex" src="/svgs/amex.svg" />
          <LogoProvider title="Discover" src="/svgs/discover.svg" />
          <LogoProvider title="UnionPay" src="/svgs/unionpay.svg" />
        </div>
        {showGuarantee && <PaymentGuarantee />}
        {ctx.modalIsOpened && (
          <PaymentSuccessfulPopup
            newUser={!!ctx.newUser}
            isFigmaBackup
            onClose={() => {
              send({
                type: "CLOSE_MODAL",
              });
              user && redirect("/account");
            }}
          />
        )}
      </div>
    </>
  );
};
